import axios from 'axios';
import { message } from 'antd';

/** Default for most API calls. Long jobs override in request interceptor. */
const DEFAULT_AXIOS_TIMEOUT_MS = 10000;
/** Must cover backend → provider script runs (see backend ZHONGMIN_SCRIPT_TIMEOUT_MS, default 600000). */
const ZHONGMIN_SCRIPT_HTTP_TIMEOUT_MS =
  Number(process.env.REACT_APP_ZHONGMIN_SCRIPT_HTTP_TIMEOUT_MS) || 600_000;
/**
 * Admin POST /admin/vouchers/sync-provider pulls many pages from provider-service; can take minutes.
 * Without this, the browser aborts at DEFAULT_AXIOS_TIMEOUT_MS with "timeout of 10000ms exceeded".
 */
const EXTERNAL_PROVIDER_SYNC_HTTP_TIMEOUT_MS =
  Number(process.env.REACT_APP_EXTERNAL_PROVIDER_SYNC_HTTP_TIMEOUT_MS) || 600_000;
/**
 * Admin POST /admin/vouchers can take a few seconds in prod even though codes
 * are now minted on demand (legacy vouchers, image upload, db round-trip).
 * Give it a generous client-side timeout so a slow network never aborts at 10s.
 */
const CREATE_VOUCHER_HTTP_TIMEOUT_MS =
  Number(process.env.REACT_APP_CREATE_VOUCHER_HTTP_TIMEOUT_MS) || 30_000;

function isZhongminRunScriptUrl(config) {
  const u = config.url || '';
  return u.includes('zhongmin-run-script');
}

function isZhongminNyyhUploadUrl(config) {
  const u = config.url || '';
  return u.includes('zhongmin-nyyh-upload');
}

function isExternalProviderSyncUrl(config) {
  const u = config.url || '';
  return u.includes('vouchers/sync-provider');
}

function isCreateVoucherUrl(config) {
  const u = config.url || '';
  const method = (config.method || '').toLowerCase();
  return method === 'post' && /\/admin\/vouchers\/?(\?.*)?$/.test(u);
}

// Create axios instance
const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api'),
  timeout: DEFAULT_AXIOS_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
instance.interceptors.request.use(
  (config) => {
    if (isZhongminRunScriptUrl(config) || isZhongminNyyhUploadUrl(config)) {
      config.timeout = ZHONGMIN_SCRIPT_HTTP_TIMEOUT_MS;
    }
    if (isExternalProviderSyncUrl(config)) {
      config.timeout = EXTERNAL_PROVIDER_SYNC_HTTP_TIMEOUT_MS;
    }
    if (isCreateVoucherUrl(config)) {
      config.timeout = CREATE_VOUCHER_HTTP_TIMEOUT_MS;
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Handle FormData for file uploads
    if (config.data instanceof FormData) {
      // Don't set Content-Type header for FormData, let browser set it with boundary
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('Response error:', {
      url: error.config?.url,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });

    const { response } = error;
    
    if (response) {
      const { status, data } = response;
      
      switch (status) {
        case 400:
          // Bad request / validation style errors
          if (data.errors && Array.isArray(data.errors)) {
            data.errors.forEach(err => message.error(err.msg || err.message || 'Validation failed'));
          } else {
            message.error(data.message || 'Bad request');
          }
          break;

        case 401: {
          // Login failure also returns 401, don't treat wrong password as session expired
          const url = error.config?.url || '';
          const isLoginAttempt =
            url.includes('/auth/login') || url.includes('/auth/admin/login');
          if (isLoginAttempt) {
            break;
          }
          localStorage.removeItem('token');
          const currentPath = window.location.pathname;
          const isAdminPath = currentPath.startsWith('/admin');
          const targetLoginPath = isAdminPath ? '/admin/login' : '/login';

          if (currentPath !== targetLoginPath) {
            message.error('Session expired. Please login again.');
            window.location.href = targetLoginPath;
          }
          break;
        }
          
        case 403:
          // Forbidden
          message.error(data.message || 'Access denied');
          break;
          
        case 404:
          // Not found
          message.error(data.message || 'Resource not found');
          break;
          
        case 422:
          // Validation error
          if (data.errors && Array.isArray(data.errors)) {
            data.errors.forEach(err => message.error(err.msg || err.message));
          } else {
            message.error(data.message || 'Validation failed');
          }
          break;
          
        case 429:
          // Rate limited
          message.error('Too many requests. Please try again later.');
          break;
          
        case 500:
          // Server error — the API usually says which operation failed.
          message.error((data && data.message) || 'Server error. Please try again later.');
          break;
          
        default:
          // Other errors
          message.error(data.message || 'An error occurred');
      }
    } else if (error.request) {
      // No HTTP response (timeout, DNS, CORS, connection reset, proxy closed, etc.)
      const url = error.config?.url || '';
      if (url.includes('zhongmin-run-script') || url.includes('zhongmin-nyyh-upload')) {
        // Caller shows a specific message; avoid duplicate generic toast + "timeout 10000ms" confusion
      } else {
        message.error('Network error. Please check your connection.');
      }
    } else {
      // Other errors
      message.error('An unexpected error occurred.');
    }

    // Every branch above has already told the user (except a failed login
    // and the Zhongmin script calls, whose callers word their own message).
    // showApiError() in utils/apiError.js checks this so a page does not add
    // a second toast for the same failure.
    const url = error.config?.url || '';
    const callerOwnsMessage =
      (response?.status === 401 && (url.includes('/auth/login') || url.includes('/auth/admin/login'))) ||
      (!response && error.request && (url.includes('zhongmin-run-script') || url.includes('zhongmin-nyyh-upload')));
    error.toastShown = !callerOwnsMessage;

    return Promise.reject(error);
  }
);

export default instance;
